/* Wizard-only highlights and landing observers. The shared engine owns the logo entrance,
   word reveal, navigation, reduced motion and its own entrance lifecycle. */
(function () {
  'use strict';
  if (window.CQWizIntro) return;
  const mounted = new WeakMap();
  const PAUSE = 'data-cqv-motion-paused';
  function bypass(location) {
    const query = new URLSearchParams(location.search);
    return query.get('start') === '1' || location.hash === '#start' || query.has('m') || /[#&]m=/.test(location.hash);
  }
  function attach(root) {
    if (mounted.has(root)) return;
    const doc = root.ownerDocument, win = doc.defaultView;
    const mq = win.matchMedia ? win.matchMedia('(prefers-reduced-motion: reduce)') : {matches: false};
    const skipEntry = bypass(win.location);
    const calm = () => !!(mq.matches || root.closest('[' + PAUSE + ']') || (win.navigator.connection && win.navigator.connection.saveData));

    // The shared renderer owns the words. Stars use colour; explicit double-equals
    // marks alone wait for the reveal completion before sweeping their full phrase.
    const seen = new WeakSet(), visible = new WeakSet(), watching = new Set();
    const intersection = win.IntersectionObserver ? new win.IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) visible.add(entry.target); else visible.delete(entry.target);
      });
      highlight();
    }, {threshold: 0.1}) : null;
    function highlight() {
      watching.forEach(title => {
        if (!root.contains(title)) { intersection.unobserve(title); watching.delete(title); }
      });
      const headings = Array.from(root.querySelectorAll('#cqw-how'));
      const screen = root.querySelector('[data-cqq-screen]');
      if (screen && (screen.getAttribute('data-cqq-screen') === 'q01' || screen.classList.contains('cqq-chapter') || screen.classList.contains('cqq-result') || screen.querySelector('h1 mark.cqv-q-said'))) {
        const title = screen.querySelector('h1');
        if (title) headings.push(title);
      }
      headings.forEach(title => {
        // The shared formatter already safely parses ==text==. Normalise only title marks,
        // leaving answer-playback marks and every other quiz untouched.
        title.querySelectorAll('mark.cqv-q-said:not(.cqw-marker)').forEach(mark => {
          mark.classList.add('cqw-marker');
          const walker = doc.createTreeWalker(mark, 4);
          let text;
          while ((text = walker.nextNode())) text.nodeValue = text.nodeValue.replace(/\u00a0/g, ' ');
        });
        const marks = Array.from(title.querySelectorAll('em,.cqw-marker'));
        if (!marks.length) return;
        marks.forEach(mark => { if (mark.tagName === 'EM' && !mark.classList.contains('cqw-hl')) mark.classList.add('cqw-hl'); });
        if (!seen.has(title)) {
          seen.add(title);
          if (intersection) { watching.add(title); intersection.observe(title); }
        }
        // An incoming heading can exist before the shared engine starts its reveal.
        // Wait for the outgoing frame to begin leaving, then use words-done directly.
        if (root.querySelector('.cqq-start-moment:not(.is-out),.cqq-ghost:not(.is-out)')) return;
        const revealing = title.classList.contains('cqq-words') && !title.classList.contains('cqq-words-done');
        // A real marker completes its 650 ms sweep during the shared word reveal.
        if (revealing && (!intersection || visible.has(title))) {
          marks.filter(mark => mark.classList.contains('cqw-marker')).forEach(mark => {
            if (!mark.classList.contains('is-on')) mark.classList.add('is-on');
          });
        }
        if (revealing && !calm()) return;
        if (!calm() && intersection && !visible.has(title)) return;
        marks.forEach(mark => {
          if (calm() && !mark.classList.contains('is-still')) mark.classList.add('is-still');
          if (!mark.classList.contains('is-on')) mark.classList.add('is-on');
        });
        if (intersection) { intersection.unobserve(title); watching.delete(title); }
      });
    }
    const highlights = win.MutationObserver ? new win.MutationObserver(highlight) : null;
    if (highlights) highlights.observe(root, {childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-cqq-screen']});

    // Landing and result pages scroll with the document; questions use the quiz frame.
    function headerTone() {
      const hero = root.querySelector('.cqw-hero');
      const header = root.querySelector('.cqv-quiz-header');
      const past = !!(hero && header && hero.getBoundingClientRect().bottom <= header.getBoundingClientRect().bottom);
      if (root.classList.contains('cqw-past-hero') !== past) root.classList.toggle('cqw-past-hero', past);
    }
    root.addEventListener('scroll', headerTone, {passive: true});
    win.addEventListener('scroll', headerTone, {passive: true});
    win.addEventListener('resize', headerTone);
    headerTone();
    const companions = root.querySelector('.cqw-how');
    const companionObserver = companions && win.IntersectionObserver ? new win.IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        companions.classList.add('cqw-companions-in'); companionObserver.disconnect();
      }
    }, {root: root.scrollHeight > root.clientHeight + 1 && /(auto|scroll)/.test(win.getComputedStyle(root).overflowY) ? root : null, threshold: 0.1}) : null;
    if (companionObserver && !calm()) {
      companions.classList.add('cqw-companions-ready'); companionObserver.observe(companions);
    }

    // Direct task links retain their immediate start. Ordinary Start never pauses the
    // shared engine, so its logo entrance and the first heading keep their native timing.
    const onStart = event => {
      if (!skipEntry || event.target !== root || !event.detail || event.detail.name !== 'cq_quiz_start') return;
      const previous = root.getAttribute(PAUSE);
      root.setAttribute(PAUSE, '');
      Promise.resolve().then(() => {
        if (previous === null) root.removeAttribute(PAUSE); else root.setAttribute(PAUSE, previous);
      });
    };
    root.addEventListener('cqq:event', onStart);
    const motion = () => { highlight(); };
    const owner = root.closest('.shopify-section') || root;
    const observer = win.MutationObserver ? new win.MutationObserver(motion) : null;
    if (observer) {
      let node = root;
      while (node) { observer.observe(node, {attributes: true, attributeFilter: [PAUSE]}); node = node.parentElement; }
    }
    function unload() {
      root.removeEventListener('cqq:event', onStart);
      if (mq.removeEventListener) mq.removeEventListener('change', motion);
      else if (mq.removeListener) mq.removeListener(motion);
      if (observer) observer.disconnect();
      if (highlights) highlights.disconnect();
      if (intersection) intersection.disconnect();
      if (companionObserver) companionObserver.disconnect();
      root.removeEventListener('scroll', headerTone); win.removeEventListener('resize', headerTone);
      win.removeEventListener('scroll', headerTone);
      watching.clear();
      owner.removeEventListener('shopify:section:unload', unload); mounted.delete(root);
    }
    if (mq.addEventListener) mq.addEventListener('change', motion);
    else if (mq.addListener) mq.addListener(motion);
    owner.addEventListener('shopify:section:unload', unload);
    mounted.set(root, unload);
    highlight();
  }
  function install(scope) {
    scope = scope || document;
    if (scope.matches && scope.matches('[data-cqq-quiz="wizard"]')) attach(scope);
    scope.querySelectorAll('[data-cqq-quiz="wizard"]').forEach(attach);
  }
  // Capture runs before the shared engine's section-load mount, including deep starts.
  document.addEventListener('shopify:section:load', event => install(event.target), true);
  window.CQWizIntro = {install};
})();
